"use client";

import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useConstant } from "@/utils/useConstant";
import { getUrl } from "aws-amplify/storage";
import { continueRender, delayRender, prefetch } from "remotion";
import { proxy, useSnapshot } from "valtio";
import { proxyMap, proxySet, useProxy } from "valtio/utils";

import { type ReadOnlyCardObjects } from "./derivedSchema";

type AssetWithExpiration = {
  url: string;
  expiresAt: number; // Unix timestamp in milliseconds
};

type AssetManagerContext = {
  data: Map<string, AssetWithExpiration>;
  fetchAssetSignedUrl: (key: string) => Promise<void>;
  refreshExpiredAssets: () => Promise<void>;
  loaded: boolean;
};
const AssetManagerContext = createContext<AssetManagerContext | undefined>(
  undefined,
);

type Props = {
  children: ReactNode;
  cardObjects: ReadOnlyCardObjects;
  prefetch?: boolean;
};

const WithInternalContext = (props: Props) => {
  const initialized = useRef(false);
  const currentlyLoadingAssets = useConstant(() => proxySet<string>());
  const data = useConstant(() => proxyMap<string, AssetWithExpiration>());
  const handle = useConstant(() => delayRender());
  const assetKeys = useConstant(() => new Set<string>());
  const refreshInProgress = useConstant(() => ({ current: false }));

  const fetchAssetSignedUrl = useConstant(
    () =>
      function (key: string) {
        if (currentlyLoadingAssets.has(key)) return Promise.resolve();
        currentlyLoadingAssets.add(key);
        assetKeys.add(key);

        const expiresIn = 3600; // 1 hour in seconds
        return getUrl({ key, options: { expiresIn } })
          .then((result) => {
            data.set(key, {
              url: result.url.toString(),
              expiresAt: Date.now() + expiresIn * 1000,
            });
          })
          .catch((error) => {
            console.error(`[AssetManager] Failed to load asset ${key}:`, error);
            throw error;
          })
          .finally(() => {
            currentlyLoadingAssets.delete(key);
          });
      },
  );

  const fetchAssetSignedUrlAndPreload = useConstant(
    () =>
      async function (key: string) {
        if (currentlyLoadingAssets.has(key)) return Promise.resolve();
        currentlyLoadingAssets.add(key);
        assetKeys.add(key);

        try {
          const expiresIn = 3600; // 1 hour in seconds
          const { url } = await getUrl({ key, options: { expiresIn } });
          data.set(key, {
            url: url.toString(),
            expiresAt: Date.now() + expiresIn * 1000,
          });
          const { waitUntilDone } = prefetch(url.toString());
          await waitUntilDone();
        } catch (error) {
          console.error(`[AssetManager] Failed to load and prefetch asset ${key}:`, error);
          throw error;
        } finally {
          currentlyLoadingAssets.delete(key);
        }
      },
  );

  const refreshExpiredAssets = useConstant(
    () =>
      async function () {
        // Prevent concurrent refresh calls
        if (refreshInProgress.current) {
          console.log('[AssetManager] Refresh already in progress, skipping');
          return;
        }

        refreshInProgress.current = true;
        try {
          const now = Date.now();
          const expiredKeys: string[] = [];

          // Find all expired assets
          assetKeys.forEach((key) => {
            const asset = data.get(key);
            if (asset && asset.expiresAt < now) {
              expiredKeys.push(key);
            }
          });

          if (expiredKeys.length === 0) return;

          console.log(`[AssetManager] Refreshing ${expiredKeys.length} expired assets`);

          // Refresh all expired assets
          await Promise.allSettled(
            expiredKeys.map((key) => {
              currentlyLoadingAssets.delete(key); // Allow refetch
              return fetchAssetSignedUrl(key);
            })
          );
        } finally {
          refreshInProgress.current = false;
        }
      },
  );

  const state = useConstant(() =>
    proxy({
      data,
      fetchAssetSignedUrl,
      refreshExpiredAssets,
      loaded: false,
    }),
  );

  useEffect(() => {
    async function init() {
      const loadFn =
        props.prefetch === true
          ? fetchAssetSignedUrlAndPreload
          : fetchAssetSignedUrl;

      await Promise.allSettled(
        props.cardObjects.flatMap((co) => {
          if (co.type === "IMAGE" || co.type === "VIDEO")
            return loadFn(co.metadata.assetURL);
          else if (co.type === "AUDIO" && co.metadata.audioSrcUrl)
            return loadFn(co.metadata.audioSrcUrl);
          else return [];
        }),
      );
      state.loaded = true;
    }

    if (!initialized.current) {
      initialized.current = true;
      init().catch(console.error);
    }
  }, []);

  // Refresh expired assets when window regains focus
  useEffect(() => {
    const handleVisibilityChange = () => {
      // state.loaded is read from valtio proxy, always returns current value
      // refreshExpiredAssets is stable (created with useConstant)
      if (document.visibilityState === 'visible' && state.loaded) {
        console.log('[AssetManager] Window focused, checking for expired assets');
        refreshExpiredAssets().catch(console.error);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps: state and refreshExpiredAssets are stable references from useConstant

  return (
    <AssetManagerContext.Provider value={state}>
      {props.children}
    </AssetManagerContext.Provider>
  );
};

export const AssetManagerProvider = (props: Props) => {
  const externalContext = useContext(AssetManagerContext);
  if (externalContext !== undefined) return props.children;
  return <WithInternalContext {...props} />;
};

export const useAssetManager = () => {
  const ctx = useContext(AssetManagerContext);
  if (ctx === undefined)
    throw new Error(
      "useAssetManager must be called from child of AssetManagerProvider",
    );
  return ctx;
};

export const useRemotionAsset = (assetURL: string) => {
  const assetManager = useAssetManager();
  const snap = useSnapshot(assetManager);
  if (assetURL === "") return undefined;
  const asset = snap.data.get(assetURL);
  return asset?.url;
};
